import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLE_LABELS } from '../../context/AuthContext';

/**
 * Admin-only quick profile tester.
 *
 * Reuses AuthContext.quickSwitch (a REAL backend login as a seeded test
 * account) + returnToOrigin — so every role is tested with its real token,
 * real permissions and real data, not a cosmetic client-side toggle.
 *
 * Rendered fixed-position on purpose: the old TestAccountBanner was removed
 * for breaking the Direction layout, so this stays out of document flow and
 * can't push anything around. Only mounted for a real admin/dg, or while a
 * quick-switch session is active (to offer the way back).
 */
const TEST_ACCOUNTS = [
  { label: 'Membre',      email: 'marie.laurent@example.com',             password: 'password123', home: '/member/home' },
  { label: 'Responsable', email: 'jeanmarc.okoye@pariscentrechretien.fr', password: 'leader123',   home: '/leader/dashboard' },
  { label: 'Scanner',     email: 'inscriptions@pariscentrechretien.fr',   password: 'scanner123',  home: '/scanner' },
];

const ADMIN_HOME = '/dg/dashboard';

export default function TestProfileSwitcher() {
  const { role, isQuickSwitched, quickSwitch, returnToOrigin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const doSwitch = useCallback(async (acc) => {
    setBusy(true);
    setError('');
    try {
      const res = await quickSwitch(acc.email, acc.password);
      if (res?.success) {
        setOpen(false);
        navigate(acc.home, { replace: true });
      } else {
        // quickSwitch self-heals the admin session on failure; just report it.
        setError(`${acc.label} : ${res?.error || 'échec de connexion'}`);
      }
    } catch (_) {
      setError(`${acc.label} : échec de connexion`);
    } finally {
      setBusy(false);
    }
  }, [quickSwitch, navigate]);

  const doReturn = useCallback(async () => {
    setBusy(true);
    try {
      await returnToOrigin();
      navigate(ADMIN_HOME, { replace: true });
    } finally {
      setBusy(false);
    }
  }, [returnToOrigin, navigate]);

  // Only an admin/dg can start a test; while switched, anyone sees the banner
  // back to admin (the stashed origin is always an admin/dg).
  const canStart = role === 'admin' || role === 'dg';
  if (!isQuickSwitched && !canStart) return null;

  const anchor = {
    position: 'fixed',
    left: 12,
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 78px)',
    zIndex: 9998,
  };

  // --- Active test session: persistent "return to admin" pill ---
  if (isQuickSwitched) {
    return (
      <div style={anchor}>
        <div className="flex items-center gap-2 rounded-full bg-amber-500 text-white shadow-lg pl-3 pr-1.5 py-1.5 text-sm font-medium">
          <span>🧪 Test&nbsp;: {ROLE_LABELS[role] || role}</span>
          <button
            type="button"
            onClick={doReturn}
            disabled={busy}
            className="rounded-full bg-white/20 hover:bg-white/30 disabled:opacity-60 px-2.5 py-1 text-xs font-semibold"
          >
            {busy ? '…' : 'Revenir admin'}
          </button>
        </div>
      </div>
    );
  }

  // --- Real admin, not switched: launcher + menu ---
  return (
    <div style={anchor}>
      {open && (
        <div className="mb-2 w-52 rounded-2xl bg-white dark:bg-neutral-900 shadow-xl ring-1 ring-black/10 overflow-hidden">
          <div className="px-3 py-2 text-xs font-semibold text-neutral-500 dark:text-neutral-400 border-b border-black/5 dark:border-white/5">
            Tester un profil
          </div>
          {TEST_ACCOUNTS.map((acc) => (
            <button
              key={acc.email}
              type="button"
              onClick={() => doSwitch(acc)}
              disabled={busy}
              className="w-full text-left px-3 py-2.5 text-sm text-neutral-800 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-60"
            >
              {acc.label}
            </button>
          ))}
          {error && (
            <p className="px-3 py-2 text-xs text-red-600 dark:text-red-400 border-t border-black/5 dark:border-white/5">
              {error}
            </p>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setError(''); }}
        className="rounded-full shadow-lg px-3.5 py-2 text-sm font-semibold text-white"
        style={{ backgroundColor: '#00A99D' }}
      >
        🧪 Profils
      </button>
    </div>
  );
}
